/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useLocation, useParams} from 'react-router-dom'
import Helmet from 'react-helmet'

import {
    Box,
    Skeleton,
    AspectRatio,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {usePages, usePage, useCategory} from '@salesforce/commerce-sdk-react'
import {Page} from '@salesforce/commerce-sdk-react/components'
import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'

import CategoryContent from './partials/category-content'
import GiftCertificate from '../gift-certificate'
import {PAGEDESIGNER_TO_COMPONENT} from '../../page-designer/pd-configs'
import {HTML_REGEX} from '../../constants'

export const PageDesignerSkeleton = () => (
    <Box p={4}>
        <Stack spacing={4}>
            <AspectRatio ratio={16 / 6}>
                <Skeleton borderRadius="md" />
            </AspectRatio>
        </Stack>
    </Box>
)

const ProductList = () => {
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)
    let searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery
    const params = useParams()

    const {data: category} = useCategory(
        {
            parameters: {id: params.categoryId}
        },
        {enabled: !!params.categoryId}
    )

    const categoryId = category?.c_alternativeUrl
        ? category?.c_alternativeUrl.match(HTML_REGEX)?.[1]
        : params?.categoryId

    const isClpPage =
        categoryId === 'shop-by-series' && params?.categoryId?.toLowerCase() === 'series'
    const isMembership = params?.categoryId?.toLowerCase() === 'memberships'

    const {
        data: clpData,
        error: clpError,
        isLoading: clpLoading
    } = usePage(
        {
            parameters: {pageId: categoryId, aspectAttributes: `{"category":"${categoryId}"}`}
        },
        {enabled: isClpPage && !isSearch}
    )

    const {
        data: plpData,
        error: plpError,
        isLoading: plpLoading
    } = usePages(
        {
            parameters: {
                aspectTypeId: 'plp',
                categoryId: categoryId,
                aspectAttributes: `{"category":"${categoryId}"}`
            }
        },
        {enabled: !isClpPage && !isSearch}
    )

    const pdPage = isClpPage ? clpData : plpData
    const error = isClpPage ? clpError : plpError
    const isLoading = isClpPage ? clpLoading : plpLoading
    const isPageDesignerPage = clpData?.regions?.length > 0 || pdPage?.data?.length > 0

    if (params.categoryId === 'giftcertpurchase') {
        return <GiftCertificate />
    }

    if (error && !isSearch) {
        let ErrorClass = error.response?.status === 404 ? HTTPNotFound : HTTPError
        throw new ErrorClass(error.response?.statusText)
    }

    if ((isMembership || isClpPage) && isLoading) {
        return <PageDesignerSkeleton />
    }

    if (isPageDesignerPage) {
        return (
            <Box className="sf-pd-product-list-page">
                <Helmet>
                    <meta name="robots" content="noindex" />
                    <title>{category?.pageTitle}</title>
                    <meta
                        name="description"
                        content={category?.pageDescription}
                        key="description"
                    />
                    <meta name="keywords" content={category?.pageKeywords} key="keywords" />
                </Helmet>
                <Page
                    page={isClpPage ? pdPage : pdPage.data[0]}
                    components={PAGEDESIGNER_TO_COMPONENT}
                />
            </Box>
        )
    }

    if (!isClpPage && !isMembership && !isPageDesignerPage) {
        return <CategoryContent />
    }

    return null
}

export default ProductList
